import { CommonModule } from '@angular/common';
import { Component, computed, Signal, inject, effect } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { provideComponentStore } from '@ngrx/component-store';
import {
    Breadcrumb,
    BreadcrumbListComponent,
    buildFolderBreadcrumbs,
    TitledContainerComponent,
} from '@r1-compass-library/components';
import { RoutingService } from '@r1-compass-library/services';
import { combineLatest, map } from 'rxjs';
import { rxEffect } from 'ngxtension/rx-effect';
import { filterNil } from 'ngxtension/filter-nil';
import { TemplateCriteriaStore } from './store/template-criteria.store';
import { DataMartTablesStore } from './store/data-mart-tables.store';
import { TemplateCriteriaListStore } from './store/template-criteria-list.store';
import { AppConstants } from '../app.constants';
import { DataMartModelsStore } from '@r1-compass-library/store/data-mart-models.store';
import { TemplateCriteriaValidationService } from './template-criteria-validation.service';
import { TemplateCriteriaValidationStateService } from './template-criteria-validation-state.service';
import { CompassRouteParent, TemplateCriteria } from '@r1-compass-library/models';
import {
    SyncValidationStateService,
    SyncValidationStateTopLevelComponent,
    TemplateCriteriaSectionName,
} from '@r1-compass-library/features/validation-sync';

@Component({
    selector: 'r1-compass-app-template-criteria',
    imports: [CommonModule, TitledContainerComponent, RouterModule, BreadcrumbListComponent],
    providers: [
        provideComponentStore(TemplateCriteriaStore),
        provideComponentStore(DataMartTablesStore),
        provideComponentStore(TemplateCriteriaListStore),
        provideComponentStore(DataMartModelsStore),
    ],
    template: `
        @let template = templateCriteriaStore.template();
        @if (showCloseButton() && template) {
            <div class="additional-template-criteria-info-bar">
                <r1-compass-lib-breadcrumb-list [breadcrumbList]="breadcrumbs()" />
                <p [style.color]="template.enabled ? 'green' : 'red'">
                    {{ template.enabled ? 'Enabled' : 'Disabled' }}
                </p>
            </div>
        }
        <r1-compass-lib-titled-container
            [title]="containerTitle()"
            [showCloseButton]="showCloseButton()"
            (close)="onTemplateClosed()">
            <router-outlet />
        </r1-compass-lib-titled-container>
    `,
    styles: `
        .additional-template-criteria-info-bar {
            display: flex;
            justify-content: space-between;
        }
        p {
            margin-top: 0;
        }
    `,
})
export class TemplateCriteriaComponent
    implements SyncValidationStateTopLevelComponent<TemplateCriteriaSectionName, TemplateCriteria>
{
    protected readonly routingService = inject(RoutingService);
    readonly templateCriteriaStore = inject(TemplateCriteriaStore);
    readonly templateCriteriaListStore = inject(TemplateCriteriaListStore);
    readonly #route = inject(ActivatedRoute);
    readonly #router = inject(Router);
    readonly #validationService = inject(TemplateCriteriaValidationService);
    readonly #validationStateService = inject(TemplateCriteriaValidationStateService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);

    constructor() {
        effect(
            () => {
                const assetId = this.routingService.assetId();
                if (assetId && assetId !== 'new' && !isNaN(Number(assetId))) {
                    const numId = Number(assetId);
                    if (this.templateCriteriaStore.template()?.id !== numId) {
                        this.templateCriteriaStore.getTemplate(numId);
                    }
                    this.#validationStateService.resetValidityOfSections(false);
                } else if (assetId === 'new') {
                    if (this.templateCriteriaStore.template() !== undefined) {
                        this.templateCriteriaStore.reset();
                    }
                    this.#validationStateService.resetValidityOfSections(true);
                } else {
                    if (this.templateCriteriaStore.template() !== undefined) {
                        this.templateCriteriaStore.reset();
                    }
                    this.#validationStateService.resetValidityOfSections(false);
                }
            },
            { debugName: 'sync template criteria store with route assetId' }
        );

        effect(
            () => {
                const routeData = this.#route.snapshot.data as CompassRouteParent;
                const invalidSections = this.#validationStateService.invalidSections();

                this.registerInvalidSections(routeData, invalidSections);
            },
            { debugName: 'Register templ crit `invalidSections` under given criteria key' }
        );
        effect(
            () => {
                const asset = this.templateCriteriaStore.template();
                const assetId = this.routingService.assetId();

                if (asset && asset.id === Number(assetId)) {
                    this.validateSectionsOnLoad(asset, this.#validationService, this.#validationStateService);
                }
            },
            { debugName: 'template criteria validation (on load)' }
        );

        rxEffect(
            combineLatest([
                this.templateCriteriaStore.template$.pipe(
                    map(template => template?.folderId),
                    filterNil()
                ),
                this.templateCriteriaListStore.usersAvailableDivisions$,
                this.templateCriteriaListStore.selectedIamDivision$.pipe(filterNil()),
            ]).pipe(
                map(([folderId, usersAvailableDivisions, selectedIamDivision]) => ({
                    folderId,
                    usersAvailableDivisions,
                    selectedIamDivision,
                }))
            ),
            res => {
                this.templateCriteriaListStore.getCurrentFolder(res);
            }
        );
    }

    breadcrumbs: Signal<Breadcrumb[]> = computed(() => {
        const currentFolder = this.templateCriteriaListStore.vm().currentFolder;
        return buildFolderBreadcrumbs(currentFolder, `/${AppConstants.TemplateCriteriaComponent.path}`, true);
    });

    registerInvalidSections(routeData: CompassRouteParent, invalidSections: TemplateCriteriaSectionName[]) {
        this.#syncValidationStateService.registerInvalidSections(routeData, invalidSections);
    }

    validateSectionsOnLoad(
        template: TemplateCriteria | undefined,
        validationService: TemplateCriteriaValidationService,
        validationStateService: TemplateCriteriaValidationStateService
    ) {
        this.#syncValidationStateService.validateSectionsOnLoad(template, validationService, validationStateService);
    }

    containerTitle: Signal<string> = computed(() => {
        if (!this.showCloseButton()) {
            return AppConstants.TemplateCriteriaComponent.mainTitle;
        }

        const currentChildPath = this.routingService.currentRoutePieces().routeWithoutParams?.split('/').at(-1);
        const subTitle =
            AppConstants.TemplateCriteriaComponent.children.find(child => child.path === currentChildPath)
                ?.containerTitle ?? '';

        if (this.routingService.assetId() === 'new') {
            return `New Template Criteria - ${subTitle}`;
        }

        const template = this.templateCriteriaStore.template();
        const entityName = template?.name || 'Template Criteria';
        return `${entityName} - ${subTitle}`;
    });

    showCloseButton: Signal<boolean> = computed(() => {
        return this.routingService.parentSegment() !== '';
    });

    onTemplateClosed() {
        const folderId = this.templateCriteriaStore.template()?.folderId ?? AppConstants.defaultRootFolderId;
        this.templateCriteriaStore.reset();
        this.#router.navigate(['.'], {
            relativeTo: this.#route,
            queryParams: { folderId: folderId },
        });
    }
}
